import type { Role } from "~/composables/useGroups"

export type PermissionCategory = "general" | "membership" | "text" | "boards" | "voice"

/** Mirrors the backend's `Vc.Common.Types.Permissions` [Flags] enum (int64) bit-for-bit. */
export const PermissionFlag = {
  ViewChannels: 2 ** 0,
  ManageChannels: 2 ** 1,
  ManageRoles: 2 ** 2,
  ManageEmojisAndStickers: 2 ** 3,
  ViewAuditLog: 2 ** 4,
  ViewServerInsights: 2 ** 5,
  ManageWebhooks: 2 ** 6,
  ManageServer: 2 ** 7,
  CreateInvite: 2 ** 8,
  ChangeNickname: 2 ** 9,
  ManageNicknames: 2 ** 10,
  KickMembers: 2 ** 11,
  BanMembers: 2 ** 12,
  ManageEvents: 2 ** 13,
  Administrator: 2 ** 14,
  SendMessages: 2 ** 15,
  SendMessagesInThreads: 2 ** 16,
  CreatePublicThreads: 2 ** 17,
  CreatePrivateThreads: 2 ** 18,
  EmbedLinks: 2 ** 19,
  AttachFiles: 2 ** 20,
  AddReactions: 2 ** 21,
  UseExternalEmoji: 2 ** 22,
  UseExternalStickers: 2 ** 23,
  MentionEveryone: 2 ** 24,
  ManageMessages: 2 ** 25,
  ManageThreads: 2 ** 26,
  ReadMessageHistory: 2 ** 27,
  SendTextToSpeechMessages: 2 ** 28,
  UseApplicationCommands: 2 ** 29,
  Connect: 2 ** 30,
  Speak: 2 ** 31,
  Video: 2 ** 32,
  StartActivities: 2 ** 33,
  UseVoiceActivity: 2 ** 34,
  PrioritySpeaker: 2 ** 35,
  MuteMembers: 2 ** 36,
  DeafenMembers: 2 ** 37,
  MoveMembers: 2 ** 38,
  RequestToSpeak: 2 ** 39,
  CreateBoardTasks: 2 ** 40,
  EditBoardTasks: 2 ** 41,
  AssignBoardTasks: 2 ** 42,
  CommentBoardTasks: 2 ** 43,
  UseBoardAgents: 2 ** 44,
  ManageBoards: 2 ** 45,
} as const satisfies Record<string, number>

export interface PermissionDefinition {
  flag: number
  name: string
  description: string
  category: PermissionCategory
}

export const PERMISSION_CATEGORY_LABELS: Record<PermissionCategory, string> = {
  general: "Общие права",
  membership: "Управление участниками",
  text: "Текстовые каналы",
  boards: "Доски",
  voice: "Голосовые каналы",
}

export const PERMISSIONS: PermissionDefinition[] = [
  {flag: 2 ** 0, name: "Просмотр каналов", description: "Позволяет просматривать каналы группы.", category: "general"},
  {flag: 2 ** 1, name: "Управление каналами", description: "Позволяет создавать, редактировать и удалять каналы.", category: "general"},
  {flag: 2 ** 2, name: "Управление ролями", description: "Позволяет создавать, редактировать и удалять роли.", category: "general"},
  // {flag: 2 ** 3, name: "Управление эмодзи и стикерами", description: "Позволяет управлять пользовательскими эмодзи и стикерами группы.", category: "general"},
  // {flag: 2 ** 4, name: "Просмотр журнала аудита", description: "Позволяет просматривать журнал аудита группы.", category: "general"},
  // {flag: 2 ** 5, name: "Просмотр статистики группы", description: "Позволяет просматривать статистику и аналитику группы.", category: "general"},
  // {flag: 2 ** 6, name: "Управление вебхуками", description: "Позволяет создавать, редактировать и удалять вебхуки.", category: "general"},
  {flag: 2 ** 7, name: "Управление группой", description: "Позволяет изменять настройки группы.", category: "general"},
  {flag: 2 ** 14, name: "Администратор", description: "Предоставляет все права в группе без ограничений.", category: "general"},

  {flag: 2 ** 8, name: "Создание приглашений", description: "Позволяет создавать приглашения в группу.", category: "membership"},
  // {flag: 2 ** 9, name: "Изменение никнейма", description: "Позволяет изменять собственный никнейм в группе.", category: "membership"},
  // {flag: 2 ** 10, name: "Управление никнеймами", description: "Позволяет изменять никнеймы других участников группы.", category: "membership"},
  {flag: 2 ** 11, name: "Исключение участников", description: "Позволяет исключать участников из группы.", category: "membership"},
  // {flag: 2 ** 12, name: "Блокировка участников", description: "Позволяет блокировать участников в группе.", category: "membership"},
  // {flag: 2 ** 13, name: "Управление событиями", description: "Позволяет создавать и управлять событиями группы.", category: "membership"},

  {flag: 2 ** 15, name: "Отправка сообщений", description: "Позволяет отправлять сообщения в текстовых каналах.", category: "text"},
  // {flag: 2 ** 16, name: "Отправка сообщений в тредах", description: "Позволяет отправлять сообщения в тредах.", category: "text"},
  // {flag: 2 ** 17, name: "Создание публичных тредов", description: "Позволяет создавать публичные треды.", category: "text"},
  // {flag: 2 ** 18, name: "Создание приватных тредов", description: "Позволяет создавать приватные треды.", category: "text"},
  // {flag: 2 ** 19, name: "Вставка ссылок", description: "Позволяет вставлять ссылки с предпросмотром (embed) в сообщениях.", category: "text"},
  {flag: 2 ** 20, name: "Прикрепление файлов", description: "Позволяет прикреплять файлы к сообщениям.", category: "text"},
  {flag: 2 ** 21, name: "Добавление реакций", description: "Позволяет добавлять реакции к сообщениям.", category: "text"},
  // {flag: 2 ** 22, name: "Внешние эмодзи", description: "Позволяет использовать эмодзи из других групп.", category: "text"},
  // {flag: 2 ** 23, name: "Внешние стикеры", description: "Позволяет использовать стикеры из других групп.", category: "text"},
  {flag: 2 ** 24, name: "Упоминание всех", description: "Позволяет упоминать @everyone, @here и все роли.", category: "text"},
  {flag: 2 ** 25, name: "Управление сообщениями", description: "Позволяет удалять и закреплять чужие сообщения.", category: "text"},
  // {flag: 2 ** 26, name: "Управление тредами", description: "Позволяет управлять тредами (архивировать, закреплять, удалять).", category: "text"},
  {flag: 2 ** 27, name: "Просмотр истории сообщений", description: "Позволяет просматривать историю сообщений канала.", category: "text"},
  // {flag: 2 ** 28, name: "Озвучивание сообщений (TTS)", description: "Позволяет отправлять сообщения с озвучиванием текста (TTS).", category: "text"},
  // {flag: 2 ** 29, name: "Использование команд приложений", description: "Позволяет использовать команды приложений (слэш-команды).", category: "text"},

  // Every board action below also needs the member's own linked tracker account (the person who
  // connected the board counts as linked) — the right alone never lets anyone write as someone else.
  {flag: 2 ** 40, name: "Создавать задачи", description: "Позволяет создавать задачи на доске своим аккаунтом трекера.", category: "boards"},
  {flag: 2 ** 41, name: "Редактировать задачи", description: "Позволяет менять заголовок, описание, поля и этап задачи, в том числе перетаскиванием.", category: "boards"},
  {flag: 2 ** 42, name: "Назначать исполнителей", description: "Позволяет назначать исполнителями людей из трекера и участников группы.", category: "boards"},
  {flag: 2 ** 43, name: "Комментировать задачи", description: "Позволяет оставлять комментарии к задачам.", category: "boards"},
  {flag: 2 ** 44, name: "Работать с агентами на доске", description: "Позволяет назначать задачи агентам и будить их упоминанием @агент в комментарии; агенты, позванные этим участником, могут работать с доской.", category: "boards"},
  {flag: 2 ** 45, name: "Управлять досками", description: "Позволяет подключать, отключать и переподключать трекер, а также менять сопоставление полей.", category: "boards"},

  {flag: 2 ** 30, name: "Подключение к голосовому каналу", description: "Позволяет подключаться к голосовому каналу.", category: "voice"},
  {flag: 2 ** 31, name: "Разговор в голосовом канале", description: "Позволяет говорить в голосовом канале.", category: "voice"},
  {flag: 2 ** 32, name: "Видео и демонстрация экрана", description: "Позволяет включать видео и демонстрацию экрана.", category: "voice"},
  // {flag: 2 ** 33, name: "Запуск активностей", description: "Позволяет запускать активности в голосовом канале.", category: "voice"},
  // {flag: 2 ** 34, name: "Голосовая активация микрофона", description: "Позволяет использовать голосовую активацию микрофона.", category: "voice"},
  // {flag: 2 ** 35, name: "Приоритетный собеседник", description: "Позволяет приглушать звук других участников при собственной речи.", category: "voice"},
  // {flag: 2 ** 36, name: "Выключение микрофона участникам", description: "Позволяет выключать микрофон другим участникам голосового канала.", category: "voice"},
  // {flag: 2 ** 37, name: "Отключение звука участникам", description: "Позволяет отключать звук другим участникам голосового канала.", category: "voice"},
  // {flag: 2 ** 38, name: "Перемещение участников", description: "Позволяет перемещать участников между голосовыми каналами.", category: "voice"},
  // {flag: 2 ** 39, name: "Запрос права высказаться", description: "Позволяет запрашивать право высказаться в трибунном канале.", category: "voice"},
]

const CATEGORY_ORDER: PermissionCategory[] = ["general", "membership", "text", "boards", "voice"]

// The subset of PERMISSIONS that makes sense as a per-channel overwrite: "text"/"boards"/"voice" flags plus
// "View channels" from "general". Server-wide-only flags (Administrator, Manage Roles, Manage
// Server, every "membership" flag, ...) aren't channel-scopable, so they're excluded here rather
// than shown as overwrites that could never actually apply.
const VIEW_CHANNELS_FLAG = 2 ** 0
export const CHANNEL_OVERWRITE_PERMISSIONS: PermissionDefinition[] = PERMISSIONS.filter(
  (p) => p.category === "text" || p.category === "boards" || p.category === "voice" || p.flag === VIEW_CHANNELS_FLAG,
)

/** Role.permissions comes back as one or more int64 chunks; combine them into a single bitmask. */
export function permissionsToMask(permissions: number[] | undefined): bigint {
  if (!permissions || permissions.length === 0) return 0n
  return permissions.reduce((acc, p) => acc | BigInt(Math.trunc(p)), 0n)
}

export function maskHasFlag(mask: bigint, flag: number): boolean {
  return (mask & BigInt(flag)) !== 0n
}

export function hasPermission(permissions: number[] | undefined, flag: number): boolean {
  return maskHasFlag(permissionsToMask(permissions), flag)
}

export function toggleMaskFlag(mask: bigint, flag: number, enabled: boolean): bigint {
  const bigFlag = BigInt(flag)
  return enabled ? (mask | bigFlag) : (mask & ~bigFlag)
}

export function groupPermissionsByCategory<T extends { category: PermissionCategory }>(permissions: T[]) {
  return CATEGORY_ORDER.map(category => ({
    category,
    label: PERMISSION_CATEGORY_LABELS[category],
    items: permissions.filter(p => p.category === category),
  })).filter(group => group.items.length > 0)
}

// All bits set — used as the "has every permission" sentinel for owners/Administrator instead of
// OR-ing every currently-known flag together, so it stays correct if more flags are added later
// without needing to touch this file. `maskHasFlag` only ever reads it via `&`, never displays or
// serializes it, so a negative bigint (infinite two's-complement leading 1s) is safe here.
const ALL_PERMISSIONS_MASK = -1n

/**
 * Combines every role a member is assigned into one effective permission bitmask. Being the group
 * owner, or holding `Administrator` on any assigned role, grants every permission at once —
 * mirrors Discord's model, where neither needs individual flags checked one by one.
 */
export function effectivePermissionMask(roles: Role[] | undefined, roleIds: string[] | undefined, isOwner: boolean): bigint {
  if (isOwner) return ALL_PERMISSIONS_MASK
  if (!roles || !roleIds || roleIds.length === 0) return 0n
  let mask = 0n
  for (const role of roles) {
    if (roleIds.includes(role.id)) mask |= permissionsToMask(role.permissions)
  }
  if (maskHasFlag(mask, PermissionFlag.Administrator)) return ALL_PERMISSIONS_MASK
  return mask
}
